/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'react-apollo'
import gql from 'graphql-tag'

import {
  Input,
  Spinner
} from '../'

import {
  decodeBase64,
  readablePhoneNumber
} from '../../utils'

class IdentityCellphoneInput extends React.PureComponent {
  static propTypes = {
    identity: PropTypes.string.isRequired,
    title: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.element
    ]),
    initialAutoPopulate: PropTypes.bool,
    className: PropTypes.string
  }

  static defaultProps = {
    title: 'Cellphone number',
    initialAutoPopulate: false
  }

  constructor(props) {
    super(props)

    this.state = {
      number: '',
      inputFeedback: null,
      feedbackCategory: null
    }
  }

  componentDidMount() {
    if (this.props.data.addresses && this.props.initialAutoPopulate) {
      this.autoPopulatePrimaryMsisdnInput(this.props.data.addresses.edges)
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevProps.data.addresses && this.props.data.addresses && this.props.initialAutoPopulate) {
      this.autoPopulatePrimaryMsisdnInput(this.props.data.addresses.edges)
    }
  }

  autoPopulatePrimaryMsisdnInput = (addresses) => {
    let currentPrimaryAddressValue = this.getCurrentPrimaryAddressValue(addresses)
    let defaultNumber = currentPrimaryAddressValue ? readablePhoneNumber(currentPrimaryAddressValue) : null
    if (defaultNumber) {
      this.changeNumber(defaultNumber, addresses)
    }
  }

  changeNumber = (number, addresses) => {
    let newNumber = number.replace(/ /g, '')  // remove spaces from input
    let newNumberLength = newNumber.length

    if (newNumberLength === 0) {
      this.setState({
        number: newNumber,
        inputFeedback: null,
        feedbackCategory: null
      })
    } else if (newNumberLength < 10) {
      this.setState({
        number: newNumber,
        inputFeedback: this.getFeedback('tooShort'),
        feedbackCategory: 'tooShort'
      })
    } else {
      if (newNumberLength === 10) {
        let matchingNumber = this.getMatchingIdentityAddress(newNumber, addresses)
        let currentPrimaryAddressValue = this.getCurrentPrimaryAddressValue(addresses ? addresses : this.props.data.addresses.edges)
        if (matchingNumber) {
          this.setState({
            number: newNumber,
            inputFeedback: matchingNumber.node.default
              ? null
              : this.getFeedback(
                  'notPrimary',
                  currentPrimaryAddressValue
                    ? readablePhoneNumber(currentPrimaryAddressValue)
                    : null
                ),
            feedbackCategory: matchingNumber.node.default
              ? 'default'
              : 'notPrimary'
          })
        } else {
          this.setState({
            number: newNumber,
            inputFeedback: this.getFeedback(
              'unknown',
              currentPrimaryAddressValue
                ? readablePhoneNumber(currentPrimaryAddressValue)
                : null
            ),
            feedbackCategory: 'unknown'
          })
        }
      } else {
        this.setState({
          number: newNumber,
          inputFeedback: newNumberLength < 10
            ? this.getFeedback('tooShort')
            : this.getFeedback('tooLong'),
          feedbackCategory: newNumberLength < 10
            ? 'tooShort'
            : 'tooLong'

        })
      }
    }
  }

  getMatchingIdentityAddress = (number, addresses) => (
    addresses
      ? addresses.find(address => readablePhoneNumber(address.node.addressValue) === number)
      : this.props.data.addresses.edges.find(address => readablePhoneNumber(address.node.addressValue) === number)
  )

  getCurrentPrimaryAddressValue = (addresses) => {
    let primaryAddress = addresses.find(address => address.node.default)

    if (primaryAddress) {
      return primaryAddress.node.addressValue
    } else {
      return null
    }
  }

  isPrimaryNumber = (number) => {
    let currentPrimary = this.props.data.addresses.edges.length > 0
      ? this.getCurrentPrimaryAddressValue(this.props.data.addresses.edges)
      : null

    if (currentPrimary) {
      return number === readablePhoneNumber(currentPrimary)
    } else {
      return false
    }
  }

  getFeedback = (category, primaryNumber) => (
    {
      'unknown': primaryNumber
        ? `This new number will replace ${primaryNumber} as primary.`
        : 'This number will be saved as primary.',
      'notPrimary': `Existing number ${primaryNumber} will no longer be primary.`,
      'tooShort': 'Length of number is shorter than expected.',
      'tooLong': 'Length of number is longer than expected.',
      'default': null
    }[category]
  )

  render() {
    if (this.props.data.loading) {
      return <Spinner className='slds-is-relative' size='small' />
    }

    if (this.props.data.error) {
      return (
        <Input
          id='error'
          value='Something went wrong'
          className='slds-text-color_error'
        />
      )
    }

    let matchingIdentityAddress = this.getMatchingIdentityAddress(this.state.number)

    return (
      <div className={this.props.className ? this.props.className : ''}>
        {this.props.title}
        <div className='slds-grid'>
          <Input
            id='identityCellphoneInput'
            value={this.state.number}
            onChange={e => this.changeNumber(e.target.value)}
            width={150}
            className='slds-m-right_large slds-size_1-of-4'
          />
          <div className='slds-size_3-of-4'>
            {this.state.inputFeedback}
          </div>
        </div>
        {this.props.render(
          this.state.number,
          this.state.feedbackCategory,
          matchingIdentityAddress ? matchingIdentityAddress.node.id : null,
          this.isPrimaryNumber(this.state.number)
        )}
      </div>
    )
  }
}

const getIdentityCellphones = gql`
  query($identity: UUID, $default: Boolean){
    addresses(identity_Id: $identity, addressType: "msisdn", default: $default) {
      edges {
        node {
          id
          addressValue
          default
        }
      }
    }
  }
`

const withData =
  graphql(getIdentityCellphones, {
    options: ({ identity, primaryNumberInput }) => ({
      variables: {
        identity: decodeBase64(identity),
        default: primaryNumberInput || undefined
      }
    })
  })

export default withData(IdentityCellphoneInput)



// WEBPACK FOOTER //
// ./src/components/identity-cellphone-input/index.js